import React, { useState, useEffect, useMemo } from 'react';
import {
  Card, Col, Drawer, Empty, Image, Progress, Row, Spin, Statistic, Table, Tabs, Tag, Typography, message,
} from 'antd';
import { CameraOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAdminCropDetailApi } from '../../api/admin';
import {
  AREA_UNITS, CROP_NAME_IMAGE, EXPENSE_PHOTO, PAYMENT_STATUS, SELLING_PHOTO, WEIGHT_UNITS,
} from '../../utils/constants';
import { formatCurrency, formatDate, getPaymentStatusColor } from '../../utils/formatters';

const { Title, Text } = Typography;

function Photo({ url }) {
  return url
    ? <Image src={url} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 4 }} />
    : <CameraOutlined style={{ color: '#ccc' }} />;
}

// Read-only view of one crop for admins: header, totals, expenses, sales and an
// expense-by-type breakdown. Deliberately has no edit/delete actions.
export default function AdminCropDetailDrawer({ cropId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState(null);

  useEffect(() => {
    if (!cropId) { setData(null); return; }
    let cancelled = false;
    setLoading(true);
    getAdminCropDetailApi({ crop_id: cropId })
      .then((res) => { if (!cancelled) setData(res.data?.data || null); })
      .catch(() => { if (!cancelled) message.error('Failed to load crop details'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [cropId]);

  const crop     = data?.crop;
  const expenses = useMemo(() => data?.expenses || [], [data]);
  const sales    = useMemo(() => data?.sales || [], [data]);
  const totalExpense = Number(data?.summary?.total_expense || 0);
  const totalSale    = Number(data?.summary?.total_sale || 0);
  const profit       = totalSale - totalExpense;
  const showAddedBy  = [...expenses, ...sales].some((r) => r.added_by_name);

  const breakdown = useMemo(() => Object.values(
    expenses.reduce((acc, e) => {
      const key = e.kharcho || 'Other';
      if (!acc[key]) acc[key] = { name: key, value: 0 };
      acc[key].value += Number(e.amount) || 0;
      return acc;
    }, {}),
  ).sort((a, b) => b.value - a.value), [expenses]);

  const days = useMemo(() => {
    if (!crop?.crop_date) return null;
    const today = dayjs();
    const hd = crop.harvest_date ? dayjs(crop.harvest_date) : null;
    const end = hd && !hd.isAfter(today, 'day') ? hd : today;
    return end.diff(dayjs(crop.crop_date), 'day');
  }, [crop]);

  const addedByColumn = showAddedBy
    ? [{ title: 'Added by', dataIndex: 'added_by_name', width: 120, render: (v) => v || '-' }]
    : [];

  const expenseColumns = [
    { title: 'Photo', dataIndex: 'photo', width: 60, render: (v) => <Photo url={v ? EXPENSE_PHOTO(v) : null} /> },
    { title: 'Type', dataIndex: 'kharcho', render: (v) => <span style={{ textTransform: 'capitalize', fontWeight: 500 }}>{v || '-'}</span> },
    { title: 'Amount', dataIndex: 'amount', align: 'right', render: (v) => <Text strong style={{ color: '#fa8c16' }}>{formatCurrency(v)}</Text> },
    { title: 'Date', dataIndex: 'expense_date', width: 110, render: formatDate },
    { title: 'Note', dataIndex: 'details', render: (v) => v || '-' },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      width: 100,
      render: (s) => (s ? <Tag color={getPaymentStatusColor(s)}>{PAYMENT_STATUS[s] || s}</Tag> : '-'),
    },
    ...addedByColumn,
  ];

  const saleColumns = [
    { title: 'Photo', key: 'photo', width: 60, render: (_, r) => <Photo url={r.photo_url || (r.photo ? SELLING_PHOTO(r.photo) : null)} /> },
    { title: 'Quantity', key: 'weight', render: (_, r) => `${r.weight ?? '-'} ${WEIGHT_UNITS[r.weightUnit] || r.weightUnit || ''}` },
    { title: 'Rate', key: 'rate', render: (_, r) => (r.rate ? `${formatCurrency(r.rate)} / ${r.rateUnit || ''}` : '-') },
    {
      title: 'Total',
      key: 'amount',
      align: 'right',
      render: (_, r) => <Text strong style={{ color: '#52c41a' }}>{formatCurrency(r.amount)}</Text>,
    },
    {
      title: 'Payment',
      dataIndex: 'payment_status',
      width: 110,
      render: (s) => (
        <Tag color={getPaymentStatusColor(s)}>
          {s === 'credit' ? 'Received' : s === 'partial' ? 'Partial' : 'Outstanding'}
        </Tag>
      ),
    },
    { title: 'Date', dataIndex: 'sell_date', width: 110, render: formatDate },
    { title: 'Buyer', dataIndex: 'buyer_name', render: (v, r) => <span style={{ textTransform: 'capitalize' }}>{v || r.vepari || '-'}</span> },
    ...addedByColumn,
  ];

  const tabs = [
    {
      key: 'expenses',
      label: `Expenses (${expenses.length})`,
      children: (
        <Table rowKey="id" columns={expenseColumns} dataSource={expenses} size="small" scroll={{ x: 800 }}
          pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }} />
      ),
    },
    {
      key: 'sales',
      label: `Sales (${sales.length})`,
      children: (
        <Table rowKey="id" columns={saleColumns} dataSource={sales} size="small" scroll={{ x: 800 }}
          pagination={{ pageSize: 20, showSizeChanger: false, hideOnSinglePage: true }} />
      ),
    },
    {
      key: 'breakdown',
      label: 'Expense breakdown',
      children: breakdown.length === 0 ? <Empty description="No expenses" /> : (
        <div style={{ maxWidth: 560 }}>
          {breakdown.map((b) => (
            <div key={b.name} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                <span style={{ textTransform: 'capitalize' }}>{b.name}</span>
                <Text strong>{formatCurrency(b.value)}</Text>
              </div>
              <Progress
                percent={totalExpense ? Math.round((b.value / totalExpense) * 100) : 0}
                strokeColor="#fa8c16"
                size="small"
              />
            </div>
          ))}
        </div>
      ),
    },
  ];

  return (
    <Drawer
      open={!!cropId}
      onClose={onClose}
      width={Math.min(1000, window.innerWidth)}
      destroyOnHidden
      title="Crop details"
    >
      <Spin spinning={loading}>
        {!crop ? (
          !loading && <Empty description="No crop data" />
        ) : (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
              {crop.image && (
                <img src={CROP_NAME_IMAGE(crop.image)} alt={crop.crop}
                  style={{ width: 64, height: 64, borderRadius: 10, objectFit: 'cover', flexShrink: 0 }} />
              )}
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {crop.crop}
                  {crop.verity && <span style={{ fontSize: 15, fontWeight: 400, color: '#555' }}>, {crop.verity}</span>}
                  <span style={{ marginLeft: 8 }}>
                    {Number(crop.status) === 1 ? <Tag color="green">Active</Tag> : <Tag>Harvested</Tag>}
                    {Number(crop.isActive) !== 1 && <Tag color="red">Deleted</Tag>}
                  </span>
                </Title>
                <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                  {[crop.farm_name, crop.plot_name, crop.area ? `${crop.area} ${AREA_UNITS[crop.area_unit] || ''}` : null]
                    .filter(Boolean).join(' · ') || '-'}
                </div>
                {crop.crop_date && (
                  <div style={{ fontSize: 13, color: '#1a6b2a', fontWeight: 500, marginTop: 2 }}>
                    Sown {formatDate(crop.crop_date)}
                    {crop.harvest_date ? ` · Harvest ${formatDate(crop.harvest_date)}` : ''}
                    {days != null ? ` · ${days} days` : ''}
                  </div>
                )}
              </div>
            </div>

            <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
              {[
                { label: 'Total Expense', value: totalExpense, color: '#fa8c16', bg: '#fff7e6' },
                { label: 'Total Sale',    value: totalSale,    color: '#52c41a', bg: '#f6ffed' },
                {
                  label: profit >= 0 ? 'Net Profit' : 'Net Loss',
                  value: Math.abs(profit),
                  color: profit >= 0 ? '#52c41a' : '#ff4d4f',
                  bg: profit >= 0 ? '#f6ffed' : '#fff1f0',
                },
              ].map((item) => (
                <Col xs={24} sm={8} key={item.label}>
                  <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }} styles={{ body: { padding: 12 } }}>
                    <Statistic title={item.label} value={formatCurrency(item.value)} valueStyle={{ color: item.color, fontSize: 18 }} />
                  </Card>
                </Col>
              ))}
            </Row>

            <Tabs items={tabs} />
          </>
        )}
      </Spin>
    </Drawer>
  );
}
